import { MARTIAL_ARTS_STYLES, AGE_GROUPS } from '../../constants';
import hubLocationsByState from '../../data/hub-locations.json';
import { DEFAULT_STATE, LAUNCHED_STATES } from '../../lib/stateCore.mjs';

// R4: data/hub-locations.json is STATE-KEYED ({ nsw: [...], vic: [...] }).
// ⚠️ RESTAMPED [2026-08-10]: this comment previously said the checked-in JSON
// "currently carries only the 'nsw' key". That is no longer true — it carries
// nsw (190 hub suburbs) AND vic (167). The stale line is why item 14 read as a
// data-availability problem when it was only ever a VOCABULARY problem.
interface HubLocationEntry { slug: string; suburb: string; count: number }
const hubsByState = hubLocationsByState as Record<string, HubLocationEntry[]>;
// The BARE vocabulary stays exactly the DEFAULT_STATE slug set, unchanged and
// still locked by src/constants/blogTags.stateKey.test.ts. Every tag that
// validated before this change validates identically after it.
const hubLocations: HubLocationEntry[] = hubsByState[DEFAULT_STATE] ?? [];

/**
 * Faceted-tag vocabularies for blog posts (item 10 — code foundation).
 *
 * Four optional facets are attached to BlogPost (src/types/blog.ts):
 *   - styles    → the directory martial-art vocabulary  (buildStyleUrl-mappable)
 *   - regions   → the suburb/location-slug vocabulary    (buildDirectoryUrl-mappable)
 *   - audience  → a small, blog-owned reader enum (EDITORIAL/relevance axis)
 *   - ageGroups → the directory age-group vocabulary (NAVIGATIONAL; maps to the ?age filter)
 *
 * Sourcing principle: REUSE the canonical vocabularies that already exist — do
 * not fork a divergent second copy.
 *   - styles    reuse constants.ts MARTIAL_ARTS_STYLES (the directory style space).
 *   - regions   reuse data/hub-locations.json (the suburb slug space).
 *   - audience  is new, so it is defined here as the single source of truth.
 *   - ageGroups reuse constants.ts AGE_GROUPS (the directory age values).
 *
 * NOTE: this module does NOT populate any tags onto posts — that is a separate
 * later step (the upsert/seed scripts). It only defines the model + vocabulary
 * so getRelatedPosts has typed facets to score.
 */

/**
 * STYLES — literal-union projection of MARTIAL_ARTS_STYLES.
 *
 * MARTIAL_ARTS_STYLES is declared `string[]` (not `as const`) and is consumed
 * elsewhere by `MARTIAL_ARTS_STYLES.includes(value: string)`
 * (lib/enrichmentMerger.ts) — tightening it to `as const` would break that call
 * (out of scope). So to get a compile-time literal union WITHOUT a directory-
 * side change, we mirror the values here as `as const`. This is NOT an
 * independent vocabulary: a drift-guard test (blogTags.test.ts) asserts this
 * list stays set-equal to MARTIAL_ARTS_STYLES, so it cannot diverge silently.
 * Values are display names → directly buildStyleUrl-mappable (toStyleSlug).
 */
export const BLOG_POST_STYLES = [
  'Aikido',
  'Boxing',
  'BJJ',
  'Capoeira',
  'Hapkido',
  'Judo',
  'Karate',
  'Kendo',
  'Kickboxing',
  'Krav Maga',
  'Kung Fu',
  'MMA',
  'Muay Thai',
  'Taekwondo',
  'Wrestling',
] as const;

export type MartialArtStyle = (typeof BLOG_POST_STYLES)[number];

// Runtime membership reuses the CANONICAL list (single source of truth), not the
// mirror — the two are pinned equal by the drift-guard test.
const STYLE_VOCAB: ReadonlySet<string> = new Set(MARTIAL_ARTS_STYLES);
export const isMartialArtStyle = (value: string): value is MartialArtStyle =>
  STYLE_VOCAB.has(value);

/**
 * REGIONS — the suburb/location-slug space (data/hub-locations.json).
 *
 * The suburb vocabulary is large (one entry per hub suburb) and JSON-sourced, so
 * a hand-maintained literal union is neither derivable from the import nor sound
 * to maintain. It is therefore controlled at the VALUE level (runtime Set) with
 * `string` typing rather than as a compile-time literal union. Values are
 * hub-location SLUGS (e.g. "adamstown"), directly buildDirectoryUrl-mappable.
 */
export type BlogPostRegion = string;

export const HUB_LOCATION_SLUGS: ReadonlySet<string> = new Set(
  hubLocations.map((entry) => entry.slug)
);

/**
 * STATE-QUALIFIED REGION TAGS (item 14, read side — operator-ruled 2026-08-10).
 *
 * ── THE PROBLEM THIS SOLVES ───────────────────────────────────────────────────
 * A bare suburb slug cannot name a Victorian suburb unambiguously, because FOUR
 * hub slugs exist in BOTH states: `burwood`, `croydon`, `epping`, `richmond`
 * (measured, not recalled: 190 nsw ∩ 167 vic hub suburbs = exactly those 4).
 * So `richmond` is not a location — it is two locations. Until now the regions
 * vocabulary was the DEFAULT_STATE slice alone, so a Victorian post could not be
 * tagged to its own suburb at all: `isBlogRegion('beaufort')` returned false for
 * a real VIC hub suburb, and `isBlogRegion('richmond')` returned true but MEANT
 * the NSW one. That is the whole of item 14.
 *
 * ── WHY THIS IS THE READ SIDE ONLY ────────────────────────────────────────────
 * The tag vocabulary is mirrored in the marketing-pipeline repo, which is not on
 * this machine and which owns the WRITE path into `blog_posts`. Changing the
 * format under them would reject their writes. So this change is deliberately
 * PURELY ADDITIVE: it makes state-qualified tags *expressible and valid*, and
 * changes the meaning of nothing that already exists. The write side follows on
 * their schedule, against a spec, not a surprise.
 *
 * ── THE FORMAT, AND WHY IT IS UNAMBIGUOUS ─────────────────────────────────────
 * `<state>-<slug>` — `nsw-richmond`, `vic-richmond`, `vic-beaufort`.
 * Verified before adoption: **0 of the 357 hub slugs begin with a state prefix**,
 * against a control of 119 slugs that DO contain a hyphen (`albion-park-rail`,
 * `avalon-beach`, …). So hyphens are common in slugs and a leading `nsw-`/`vic-`
 * is never one of them — the first hyphen is always the state boundary and the
 * remainder is always the bare slug. 357 qualified tags, 0 internal collisions.
 *
 * ── SELF-ARMING ───────────────────────────────────────────────────────────────
 * Built by iterating LAUNCHED_STATES, not a literal list, so the next state
 * launch makes its suburbs taggable on the launch commit with no edit here.
 * (Deliberate: this repo's recurring defect is guards shaped like the incident
 * rather than the class — see the coordination protocol §3.4(f).)
 */
export const STATE_QUALIFIED_REGION_TAGS: ReadonlySet<string> = new Set(
  (LAUNCHED_STATES as readonly string[]).flatMap((state) =>
    (hubsByState[state] ?? []).map((entry) => `${state}-${entry.slug}`)
  )
);

/**
 * Accepts BOTH forms:
 *   - a BARE slug, which continues to mean a DEFAULT_STATE suburb exactly as before;
 *   - a STATE-QUALIFIED tag, `<state>-<slug>`, for any launched state.
 * Purely widening — every value that validated before still validates, with the
 * same meaning. Unknown values are still rejected.
 */
export const isBlogRegion = (value: string): boolean =>
  HUB_LOCATION_SLUGS.has(value) || STATE_QUALIFIED_REGION_TAGS.has(value);

/**
 * Resolve a region tag to the (state, slug) pair a URL builder needs — the piece
 * that makes a state-qualified tag *useful* rather than merely valid, since
 * buildDirectoryUrl takes a state and a bare slug, not a tag.
 *
 * A bare slug resolves to DEFAULT_STATE (its historical meaning, preserved).
 * Returns null for anything not in either vocabulary, so callers cannot
 * accidentally construct a URL for an unknown suburb.
 */
export const resolveBlogRegion = (
  value: string,
): { state: string; slug: string } | null => {
  if (HUB_LOCATION_SLUGS.has(value)) return { state: DEFAULT_STATE, slug: value };
  if (!STATE_QUALIFIED_REGION_TAGS.has(value)) return null;
  // Split at the FIRST hyphen: the prefix is the state, the remainder is the bare
  // slug (which may itself contain hyphens — `vic-albion-park-rail`). Membership
  // in STATE_QUALIFIED_REGION_TAGS is already proven above, so this cannot mis-split.
  const cut = value.indexOf('-');
  return { state: value.slice(0, cut), slug: value.slice(cut + 1) };
};

/**
 * AUDIENCE — small, blog-owned reader enum (operator-locked). New vocabulary, so
 * this IS the single source of truth: a true compile-time literal union.
 */
export const BLOG_AUDIENCES = ['beginner', 'parent', 'practitioner'] as const;

export type BlogAudience = (typeof BLOG_AUDIENCES)[number];

const AUDIENCE_VOCAB: ReadonlySet<string> = new Set(BLOG_AUDIENCES);
export const isBlogAudience = (value: string): value is BlogAudience =>
  AUDIENCE_VOCAB.has(value);

/**
 * AGE-GROUP — the NAVIGATIONAL/URL axis, distinct from `audience` (the editorial axis).
 *
 * Maps to the directory's LIVE `?age` filter: hooks/useDirectoryFilters parses
 * `searchParams.get('age').split(',')` and DirectoryPage writes `params.set('age',
 * ageGroups.join(','))`. So a tagged post can deep-link to age-filtered gym results
 * (e.g. a kids post → /nsw/{suburb}/gyms?age=Children%20(5-12)). Values are the directory's
 * age DISPLAY strings so they encode into a working filter.
 *
 * Like styles, it is a drift-guarded `as const` MIRROR of the canonical AGE_GROUPS
 * (constants.ts is `string[]`, not `as const`, and is consumed by
 * `AGE_GROUPS.includes(value: string)` in lib/enrichmentMerger.ts — tightening it would
 * break that; out of scope). Runtime membership reuses the canonical AGE_GROUPS.
 *
 * Boundary vs `audience`: audience = WHO it's editorially for (drives getRelatedPosts
 * scoring); ageGroups = WHICH directory age filter to target (navigation; does NOT score
 * relatedness). Complementary — a 'parent' audience post may carry 'Children (5-12)'.
 */
export const BLOG_AGE_GROUPS = [
  'Children (5-12)',
  'Teens (13-17)',
  'Adults (18+)',
] as const;

export type BlogAgeGroup = (typeof BLOG_AGE_GROUPS)[number];

const AGE_GROUP_VOCAB: ReadonlySet<string> = new Set(AGE_GROUPS);
export const isBlogAgeGroup = (value: string): value is BlogAgeGroup =>
  AGE_GROUP_VOCAB.has(value);
